if (!customElements.get('mirthloom-bundle-builder')) {
  customElements.define(
    'mirthloom-bundle-builder',
    class MirthloomBundleBuilder extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';
        this.root = this.closest('.ml-product');
        this.product = JSON.parse(this.querySelector('[data-ml-product-json]').textContent);
        this.variantById = new Map(this.product.variants.map((variant) => [String(variant.id), variant]));
        this.quantity = 1;
        this.primaryOptions = this.querySelector('[data-ml-primary-options]');
        this.plans = this.querySelector('[data-ml-plans]');
        this.slots = Array.from(this.querySelectorAll('[data-slot]'));
        this.submit = this.querySelector('[data-ml-submit]');
        this.buyNowButton = this.querySelector('[data-ml-buy-now]');
        this.mobileSubmit = this.querySelector('[data-ml-mobile-submit]');
        this.mobilePrice = this.querySelector('[data-ml-mobile-price]');
        this.mobileStock = this.querySelector('[data-ml-mobile-stock]');
        this.error = this.querySelector('[data-ml-error]');
        this.submitLabel = this.submit?.dataset.submitLabel || 'ADD TO CART';
        this.soldOutLabel = this.submit?.dataset.soldOutLabel || 'Sold out';
        this.moneyFormat = this.root.dataset.moneyFormat || '${{amount}}';
        this.activeVariantId = String(
          this.primaryOptions?.querySelector('.is-selected[data-variant-id]')?.dataset.variantId
            || this.product.variants.find((variant) => this.isVariantSellable(variant))?.id
            || ''
        );

        this.populateSlotSelects();
        this.syncUntouchedSlots(this.activeVariantId);
        this.bindEvents();
        this.slots.forEach((slot) => this.updateSlot(slot));
        this.updatePrimaryVariant(this.activeVariantId, true);
        this.updateState();
      }

      bindEvents() {
        this.root.querySelectorAll('[data-media-target]').forEach((button) => {
          button.addEventListener('click', () => {
            const mediaId = String(button.dataset.mediaTarget || '');
            this.showMedia(mediaId);
            const matchingVariant = this.product.variants.find((variant) => (
              String(variant.featured_media_id || '') === mediaId && this.isVariantSellable(variant)
            ));
            if (matchingVariant) this.selectPrimaryVariant(String(matchingVariant.id), false);
          });
        });

        this.primaryOptions?.addEventListener('click', (event) => {
          const button = event.target.closest('[data-variant-id]');
          if (!button || button.disabled) return;
          this.selectPrimaryVariant(String(button.dataset.variantId), true);
        });

        this.plans?.addEventListener('click', (event) => {
          const plan = event.target.closest('[data-bundle-quantity]');
          if (!plan) return;
          this.quantity = Number(plan.dataset.bundleQuantity);
          this.plans.querySelectorAll('[data-bundle-quantity]').forEach((item) => {
            const selected = item === plan;
            item.classList.toggle('is-selected', selected);
            item.setAttribute('aria-checked', String(selected));
          });
          this.slots.forEach((slot, index) => { slot.hidden = index >= this.quantity; });
          this.syncUntouchedSlots(this.activeVariantId);
          this.updateState();
        });

        this.slots.forEach((slot) => {
          slot.addEventListener('change', (event) => {
            if (!event.target.matches('select')) return;
            slot.dataset.userSelected = 'true';
            this.updateSlot(slot);
            this.updateState();
          });
        });

        this.submit?.addEventListener('click', () => this.addBundle(false));
        this.mobileSubmit?.addEventListener('click', () => this.addBundle(false));
        this.buyNowButton?.addEventListener('click', () => this.addBundle(true));
      }

      selectPrimaryVariant(variantId, showMedia = true) {
        const variant = this.variantById.get(String(variantId));
        if (!this.isVariantSellable(variant)) return;
        this.activeVariantId = String(variant.id);
        this.primaryOptions?.querySelectorAll('[data-variant-id]').forEach((item) => {
          const selected = String(item.dataset.variantId) === this.activeVariantId;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-checked', String(selected));
        });
        this.syncUntouchedSlots(this.activeVariantId);
        this.updatePrimaryVariant(this.activeVariantId, showMedia);
        this.updateState();
      }

      populateSlotSelects() {
        this.slots.forEach((slot) => {
          const select = slot.querySelector('select');
          if (!select) return;
          const existing = new Set(Array.from(select.options).map((option) => String(option.value)));
          this.product.variants.forEach((variant) => {
            if (existing.has(String(variant.id))) return;
            const option = document.createElement('option');
            option.value = String(variant.id);
            option.textContent = `${variant.title} - ${this.formatMoney(variant.price)}`;
            option.disabled = !this.isVariantSellable(variant);
            select.append(option);
          });
        });
      }

      syncUntouchedSlots(variantId) {
        if (!variantId) return;
        this.slots.forEach((slot) => {
          if (slot.dataset.userSelected === 'true') return;
          const select = slot.querySelector('select');
          const option = select?.querySelector(`option[value="${variantId}"]`);
          if (!select || !option || option.disabled) return;
          select.value = String(variantId);
          this.updateSlot(slot);
        });
      }

      selectedVariantForSlot(slot) {
        const selectedId = slot.querySelector('select')?.value;
        return this.variantById.get(String(selectedId || '')) || null;
      }

      selectedVariants(quantity = this.quantity) {
        return this.slots.slice(0, quantity).map((slot) => this.selectedVariantForSlot(slot)).filter(Boolean);
      }

      selectedItems() {
        return this.selectedVariants().map((variant, index) => ({
          id: Number(variant.id),
          quantity: 1,
          properties: { _gift_row: String(index + 1) },
        }));
      }

      isVariantSellable(variant) {
        if (!variant || !variant.available) return false;
        return !(variant.inventory_management && Number(variant.inventory_quantity) <= 0);
      }

      inventoryMessage(variant) {
        if (!variant) return '';
        if (!this.isVariantSellable(variant)) return 'Sold out';
        if (!variant.inventory_management) return '';
        const quantity = Number(variant.inventory_quantity);
        if (quantity <= 10) return `Low stock - Only ${quantity} left in this style`;
        if (quantity <= 50) return `Only ${quantity} left in this style`;
        return 'In stock · packing 1-2 days';
      }

      updatePrimaryVariant(variantId, showMedia) {
        const variant = this.variantById.get(String(variantId));
        if (!variant) return;
        const selectedStyle = this.root.querySelector('[data-ml-selected-style]');
        const price = this.root.querySelector('[data-ml-price]');
        const compare = this.root.querySelector('[data-ml-compare-price]');
        const saving = this.root.querySelector('[data-ml-discount]');
        const inventory = this.root.querySelector('[data-ml-current-inventory]');
        const comparePrice = Math.max(Number(variant.compare_at_price || 0), Number(variant.price || 0));
        const discount = comparePrice > variant.price ? Math.round((comparePrice - variant.price) * 100 / comparePrice) : 0;

        if (selectedStyle) selectedStyle.textContent = variant.title;
        if (price) price.textContent = this.formatMoney(variant.price);
        if (compare) {
          compare.textContent = this.formatMoney(comparePrice);
          compare.hidden = comparePrice <= variant.price;
        }
        if (saving) {
          saving.textContent = `SAVE ${discount}%`;
          saving.hidden = discount <= 0;
        }
        if (inventory) {
          inventory.textContent = this.inventoryMessage(variant);
          inventory.hidden = !inventory.textContent;
          inventory.classList.toggle('is-low', Boolean(variant.inventory_management && Number(variant.inventory_quantity) <= 10));
        }
        this.root.querySelectorAll('[data-ml-points]').forEach((list) => {
          list.hidden = list.dataset.mlPoints !== variant.product_type;
        });
        if (showMedia) this.showMedia(variant.featured_media_id);
      }

      updateSlot(slot) {
        const variant = this.selectedVariantForSlot(slot);
        const option = slot.querySelector('select')?.selectedOptions[0];
        const preview = slot.querySelector('[data-ml-slot-preview]');
        const unitPrice = slot.querySelector('[data-ml-slot-price]');
        const inventory = slot.querySelector('[data-ml-slot-inventory]');
        if (!variant || !option) return;
        if (preview) {
          const imageUrl = option.dataset.imageUrl || variant.image_url || '';
          if (imageUrl) {
            const image = document.createElement('img');
            image.src = imageUrl;
            image.alt = variant.title;
            image.width = 160;
            image.height = 160;
            image.loading = 'eager';
            preview.replaceChildren(image);
          } else preview.replaceChildren();
        }
        if (unitPrice) unitPrice.textContent = `${this.formatMoney(variant.price)} each`;
        if (inventory) {
          inventory.textContent = this.inventoryMessage(variant);
          inventory.hidden = !inventory.textContent;
          inventory.classList.toggle('is-low', Boolean(variant.inventory_management && Number(variant.inventory_quantity) <= 10));
        }
      }

      totalsFor(quantity = this.quantity) {
        const variants = this.selectedVariants(quantity);
        if (variants.length !== quantity) return null;
        const price = variants.reduce((sum, variant) => sum + Number(variant.price || 0), 0);
        const compare = variants.reduce((sum, variant) => sum + Math.max(Number(variant.compare_at_price || 0), Number(variant.price || 0)), 0);
        return { price, compare, saving: Math.max(compare - price, 0) };
      }

      updatePlanPrices() {
        [1, 2, 3].forEach((quantity) => {
          const totals = this.totalsFor(quantity);
          if (!totals) return;
          const price = this.root.querySelector(`[data-ml-plan-price="${quantity}"]`);
          const compare = this.root.querySelector(`[data-ml-plan-compare="${quantity}"]`);
          const saving = this.root.querySelector(`[data-ml-plan-saving="${quantity}"]`);
          if (price) price.textContent = this.formatMoney(totals.price);
          if (compare) {
            compare.textContent = this.formatMoney(totals.compare);
            compare.hidden = totals.compare <= totals.price;
          }
          if (saving) {
            saving.textContent = totals.saving > 0 ? `Save ${this.formatMoney(totals.saving)}` : '';
            saving.hidden = totals.saving <= 0;
          }
        });
      }

      validateInventory(variants) {
        const demand = new Map();
        variants.forEach((variant) => demand.set(String(variant.id), (demand.get(String(variant.id)) || 0) + 1));
        for (const [id, requested] of demand) {
          const variant = this.variantById.get(id);
          if (!this.isVariantSellable(variant)) return { valid: false, message: `${variant?.title || 'This style'} is sold out.` };
          if (variant.inventory_management && requested > Number(variant.inventory_quantity)) {
            return { valid: false, message: `Only ${variant.inventory_quantity} ${variant.title} left. Please choose another style.` };
          }
        }
        return { valid: true, message: '' };
      }

      updateState() {
        this.slots.forEach((slot) => this.updateSlot(slot));
        this.updatePlanPrices();
        const variants = this.selectedVariants();
        const inventory = this.validateInventory(variants);
        const totals = this.totalsFor();
        const available = variants.length === this.quantity && inventory.valid && Boolean(totals);
        const itemWord = this.quantity === 1 ? 'ITEM' : 'ITEMS';
        const label = available ? `${this.submitLabel} · ${this.quantity} ${itemWord} · ${this.formatMoney(totals.price)}` : this.soldOutLabel;
        if (this.submit) {
          this.submit.disabled = !available;
          const text = this.submit.querySelector('[data-ml-submit-text]');
          if (text) text.textContent = label;
        }
        if (this.buyNowButton) this.buyNowButton.disabled = !available;
        if (this.mobileSubmit) {
          this.mobileSubmit.disabled = !available;
          this.mobileSubmit.textContent = available ? this.submitLabel : this.soldOutLabel;
        }
        if (this.mobilePrice) this.mobilePrice.textContent = totals ? this.formatMoney(totals.price) : '';
        if (this.mobileStock) {
          const stockMessage = this.quantity === 1 ? this.inventoryMessage(variants[0]) : '';
          this.mobileStock.textContent = stockMessage;
          this.mobileStock.hidden = !stockMessage;
        }
        this.showError(inventory.message);
      }

      formatMoney(cents) {
        const format = this.moneyFormat;
        const placeholder = format.match(/\{\{\s*([a-z_]+)\s*\}\}/i);
        if (!placeholder) return `$${(Number(cents || 0) / 100).toFixed(2)}`;
        const amount = Number(cents || 0) / 100;
        const fixed = amount.toFixed(placeholder[1].includes('no_decimals') ? 0 : 2).split('.');
        fixed[0] = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return format.replace(placeholder[0], fixed.length > 1 ? `${fixed[0]}.${fixed[1]}` : fixed[0]).trim();
      }

      showMedia(mediaId) {
        if (!mediaId) return;
        this.root.querySelectorAll('.ml-product__media-item[data-media-id]').forEach((media) => {
          const active = media.dataset.mediaId === String(mediaId);
          media.hidden = !active;
          media.classList.toggle('is-active', active);
        });
        this.root.querySelectorAll('[data-media-target]').forEach((thumb) => {
          const active = thumb.dataset.mediaTarget === String(mediaId);
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-pressed', String(active));
        });
      }

      async addBundle(checkoutAfterAdd) {
        const variants = this.selectedVariants();
        const inventory = this.validateInventory(variants);
        const items = this.selectedItems();
        if (items.length !== this.quantity || !inventory.valid || this.submit?.disabled) return;
        this.setLoading(true);
        this.showError('');
        const cart = document.querySelector('cart-drawer');
        try {
          const payload = { items };
          if (cart && !checkoutAfterAdd) {
            payload.sections = cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
            cart.setActiveElement(this.submit);
          }
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
            body: JSON.stringify(payload),
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add these gifts to your cart.');
          if (checkoutAfterAdd) window.location.assign('/checkout');
          else if (cart && result.sections) cart.renderContents(result);
          else window.location.assign(window.routes?.cart_url || '/cart');
        } catch (error) {
          this.showError(error.message || 'Something went wrong. Please try again.');
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(loading) {
        if (this.submit) {
          this.submit.classList.toggle('is-loading', loading);
          this.submit.disabled = loading;
        }
        if (this.mobileSubmit) {
          this.mobileSubmit.classList.toggle('is-loading', loading);
          this.mobileSubmit.disabled = loading;
          if (loading) this.mobileSubmit.textContent = 'ADDING...';
        }
        if (!loading) this.updateState();
      }

      showError(message) {
        if (!this.error) return;
        this.error.textContent = message || '';
        this.error.hidden = !message;
      }
    }
  );
}
